// =============================================================================
// PaymentSuccess — Public patient-facing receipt page (PAY-14)
// =============================================================================
// Route: /payment-success?token=<pay_token>
// Auth:  None (public). The pay_token acts as an opaque bearer for this page.
//
// Calls pay-checkout-status Edge Function with the token, then renders a
// friendly confirmation with amount, date, and Stripe receipt link.
// Linear: CLINIO-210
// =============================================================================

import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { CheckCircle, ExternalLink, Loader2, AlertCircle } from 'lucide-react';
import { env } from '@/lib/env';
import { ButtonAnchor } from '@/components/ui/Button';
import { PAGE_TITLE } from '@/components/ui/pageChrome';

// ── Types ──────────────────────────────────────────────────────────────────

interface CheckoutStatus {
  status:       string;
  checkout_url?: string;
  expires_at?:  string;
}

type State =
  | { phase: 'loading' }
  | { phase: 'success'; data: CheckoutStatus }
  | { phase: 'error';   message: string };

// ── Component ──────────────────────────────────────────────────────────────

export function PaymentSuccess() {
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';

  const [state, setState] = useState<State>({ phase: 'loading' });

  useEffect(() => {
    if (!token || token.length < 8) {
      setState({ phase: 'error', message: 'Ugyldigt betalingslink.' });
      return;
    }

    let cancelled = false;

    async function fetchStatus() {
      try {
        const resp = await fetch(
          `${env.VITE_SUPABASE_URL}/functions/v1/pay-checkout-status?pay_token=${encodeURIComponent(token)}`,
          {
            method: 'GET',
            headers: { apikey: env.VITE_SUPABASE_ANON_KEY },
          },
        );

        const json = (await resp.json()) as {
          ok: boolean;
          data?: CheckoutStatus;
          error?: { message?: string };
        };

        if (cancelled) return;

        if (!resp.ok || !json.ok || !json.data) {
          setState({
            phase: 'error',
            message: json.error?.message ?? 'Kvitteringen kunne ikke hentes.',
          });
          return;
        }

        setState({ phase: 'success', data: json.data });
      } catch {
        if (!cancelled) {
          setState({ phase: 'error', message: 'Netværksfejl — prøv igen.' });
        }
      }
    }

    fetchStatus();
    return () => { cancelled = true; };
  }, [token]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-gray-50 px-4 dark:bg-gray-900">
      <div className="w-full max-w-md">
        {state.phase === 'loading' && <LoadingCard />}
        {state.phase === 'success' && <SuccessCard data={state.data} />}
        {state.phase === 'error' && <ErrorCard message={state.message} />}
      </div>
    </div>
  );
}

// ── Sub-cards ──────────────────────────────────────────────────────────────

function LoadingCard() {
  return (
    <div className="rounded-2xl border border-gray-200 bg-white p-8 text-center shadow-sm dark:border-gray-700 dark:bg-gray-800">
      <Loader2 className="mx-auto h-10 w-10 animate-spin text-indigo-500" />
      <p className="mt-4 text-sm text-gray-500 dark:text-gray-400">
        Henter betalingskvittering…
      </p>
    </div>
  );
}

function SuccessCard({ data }: { data: CheckoutStatus }) {
  const isPaid = data.status === 'paid' || data.status === 'succeeded' || data.status === 'completed';

  return (
    <div className="rounded-2xl border border-gray-200 bg-white p-8 shadow-sm dark:border-gray-700 dark:bg-gray-800">
      {/* Header */}
      <div className="mb-6 flex flex-col items-center text-center">
        <div className={`mb-4 flex h-16 w-16 items-center justify-center rounded-full ${
          isPaid
            ? 'bg-green-100 dark:bg-green-900/30'
            : 'bg-yellow-100 dark:bg-yellow-900/30'
        }`}>
          <CheckCircle className={`h-8 w-8 ${
            isPaid ? 'text-green-600 dark:text-green-400' : 'text-yellow-600 dark:text-yellow-400'
          }`} />
        </div>
        <h1 className={PAGE_TITLE}>
          {isPaid ? 'Betaling gennemført' : 'Betalingsstatus'}
        </h1>
        <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
          {isPaid
            ? 'Tak for din betaling. Du vil modtage en kvittering på e-mail.'
            : `Status: ${data.status}`}
        </p>
      </div>

      {/* Receipt download */}
      {data.checkout_url && (
        <ButtonAnchor
          href={data.checkout_url}
          target="_blank"
          rel="noopener noreferrer"
          size="full"
        >
          <ExternalLink />
          Download kvittering
        </ButtonAnchor>
      )}

      <p className="mt-6 text-center text-xs text-gray-400 dark:text-gray-500">
        Betaling behandlet sikkert via Clinio Pay · Stripe
      </p>
    </div>
  );
}

function ErrorCard({ message }: { message: string }) {
  return (
    <div className="rounded-2xl border border-red-200 bg-white p-8 shadow-sm dark:border-red-900/30 dark:bg-gray-800">
      <div className="mb-4 flex flex-col items-center text-center">
        <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-red-100 dark:bg-red-900/30">
          <AlertCircle className="h-8 w-8 text-red-600 dark:text-red-400" />
        </div>
        <h1 className="text-xl font-bold text-gray-900 dark:text-white">Noget gik galt</h1>
        <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">{message}</p>
      </div>
      <p className="mt-4 text-center text-xs text-gray-400 dark:text-gray-500">
        Kontakt din klinik hvis problemet fortsætter.
      </p>
    </div>
  );
}
